import React, { useEffect, useState } from "react";
import { Mail, Send, Plus } from "lucide-react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminMarketing() {
  const [tab, setTab] = useState("subscribers");
  const [subs, setSubs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [draft, setDraft] = useState({ subject: "", preview: "", sender_name: "Maison Évaren", audience: "all", content: "" });

  const loadCampaigns = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadCampaigns();
  }, []);

  const createCampaign = async () => {
    if (!draft.subject) return toast.error("Subject is required.");
    try { await api.post("/admin/campaigns", draft); toast.success("Campaign saved as draft."); setDraft({ subject: "", preview: "", sender_name: "Maison Évaren", audience: "all", content: "" }); loadCampaigns(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const send = async (id) => {
    const { data } = await api.post(`/admin/campaigns/${id}/send`);
    toast.success(`Queued to ${data.recipients} recipients. ${data.note}`); loadCampaigns();
  };

  const tabs = [["subscribers", `Subscribers (${subs.length})`], ["customers", `Customers (${customers.length})`], ["campaigns", "Email Campaigns"]];
  const inp = "w-full h-10 px-3 border border-neutral-300 rounded text-sm";

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Newsletter & Campaigns</h1>
      <div className="flex gap-2 mb-6 border-b border-neutral-200">
        {tabs.map(([k, l]) => <button key={k} data-testid={`marketing-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm ${tab === k ? "border-b-2 border-neutral-900 font-medium" : "text-neutral-500"}`}>{l}</button>)}
      </div>

      {tab === "subscribers" && (subs.length === 0 ? <Empty t="No subscribers yet" /> : (
        <div className="bg-white border border-neutral-200 rounded-lg divide-y divide-neutral-100">
          {subs.map((s) => <div key={s.id} className="flex items-center justify-between p-4 text-sm"><span className="flex items-center gap-2"><Mail size={14} className="text-neutral-400" />{s.email}</span><span className="text-xs text-neutral-400 uppercase">{s.source_storefront}</span></div>)}
        </div>
      ))}

      {tab === "customers" && (customers.length === 0 ? <Empty t="No customers yet" /> : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm"><thead className="bg-neutral-50 text-neutral-500 text-xs uppercase"><tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th></tr></thead>
            <tbody>{customers.map((c) => <tr key={c.id} className="border-t border-neutral-100"><td className="p-4">{c.name}</td><td className="p-4 text-neutral-600">{c.email}</td></tr>)}</tbody>
          </table>
        </div>
      ))}

      {tab === "campaigns" && (
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
          <div className="bg-white border border-neutral-200 rounded-lg p-6 h-fit">
            <h2 className="font-serif text-xl mb-4">New Campaign</h2>
            <label className="text-xs uppercase text-neutral-500">Subject</label>
            <input data-testid="campaign-subject" className={`${inp} mb-3`} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            <label className="text-xs uppercase text-neutral-500">Preview Text</label>
            <input className={`${inp} mb-3`} value={draft.preview} onChange={(e) => setDraft({ ...draft, preview: e.target.value })} />
            <label className="text-xs uppercase text-neutral-500">Audience</label>
            <select data-testid="campaign-audience" className={`${inp} mb-3`} value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value })}>
              <option value="all">All Subscribers</option><option value="jewelry">Jewelry</option><option value="men">Men</option><option value="women">Women</option>
            </select>
            <label className="text-xs uppercase text-neutral-500">Content</label>
            <textarea className={`${inp} h-32 py-2 mb-4`} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
            <button data-testid="campaign-create" onClick={createCampaign} className="w-full bg-neutral-900 text-white h-10 rounded text-sm flex items-center justify-center gap-2"><Plus size={15} /> Save Draft</button>
          </div>
          <div>
            {campaigns.length === 0 ? <Empty t="No campaigns yet" /> : (
              <div className="space-y-3">
                {campaigns.map((c) => (
                  <div key={c.id} className="bg-white border border-neutral-200 rounded-lg p-4 flex items-center justify-between">
                    <div><div className="font-medium">{c.subject}</div><div className="text-xs text-neutral-400 mt-1">{c.audience} · {c.status}{c.recipients ? ` · ${c.recipients} sent` : ""}</div></div>
                    {c.status === "draft" && <button data-testid={`campaign-send-${c.id}`} onClick={() => send(c.id)} className="flex items-center gap-1.5 text-sm bg-neutral-900 text-white px-4 h-9 rounded"><Send size={13} /> Send</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
const Empty = ({ t }) => <div className="bg-white border border-neutral-200 rounded-lg p-16 text-center text-neutral-500">{t}</div>;
